import React, { useState } from 'react';
import { useConsent } from '../../hooks/useConsent';
import Button from './Button';
import Card from './Card';
import LoadingSpinner from './LoadingSpinner';
import styles from './ConsentGate.module.css';

/**
 * Blocks a monitored activity until the user has explicitly opted in to
 * interaction monitoring. Renders `children` only once consent is granted.
 *
 * NOTE: the copy below is a PLACEHOLDER pending legal/privacy review. The
 * consent *version* is server-controlled (`monitoring_consent_version`); bump it
 * when the wording changes materially and users are re-asked automatically.
 */
export default function ConsentGate({ children, consentType = 'interaction_monitoring' }) {
  const { status, grant } = useConsent(consentType);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (status === 'loading') {
    return (
      <div className={styles.wrap}>
        <LoadingSpinner />
      </div>
    );
  }

  if (status === 'granted') {
    return children;
  }

  const onAgree = async () => {
    setSubmitting(true);
    setError('');
    try {
      await grant();
    } catch (e) {
      setError('Could not save your choice. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <Card title="Before you start: a note on how this works">
        <p className={styles.body}>
          To keep assessments fair and to understand how you learn, Novara records how you
          interact with activities — for example how you type, edit, and pace your answers, and
          timing signals as you work through a session.
        </p>
        <ul className={styles.list}>
          <li>This is used to support fair scoring and to personalize your learning.</li>
          <li>It is not sold, and it is not shared for advertising.</li>
          <li>You can withdraw consent later in your settings; some activities may be unavailable without it.</li>
        </ul>
        {status === 'error' && (
          <p className={styles.error}>We couldn&apos;t check your consent status. You can still choose below.</p>
        )}
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.actions}>
          <Button onClick={onAgree} loading={submitting} disabled={submitting}>
            I agree — continue
          </Button>
        </div>
      </Card>
    </div>
  );
}
